import {
  Breadcrumb,
  Button,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  Input,
  LockClosedIcon,
  PageHero,
  PhoneIcon,
  routeToUrl,
  success,
  UserCircleIcon,
  UserIcon,
  useAuthStore,
  useCustomForm,
  useState,
} from "@";
import { profileSchema } from "./utils/validation";
import { saveNewProfileInfo } from "./utils/helpers";
import { saveProfileInfo, updateProfileOTP } from "./utils/api";

const Profile = () => {
  const links = [{ name: "الملف الشخصي" }];
  const [showPassword, setShowPassword] = useState(false);
  const { user, setUser } = useAuthStore();

  const profileInfo = {
    password: "",
    id: user?.id,
    email: user?.email,
    displayName: user?.displayName,
    phoneNumber: user?.phoneNumber,
  };

  const { errors, register, handleSubmit, isSubmitting } = useCustomForm(
    profileInfo,
    profileSchema,
  );

  const submit = async (data) => {
    try {
      const { data: response } = await saveProfileInfo(data);
      if (response?.requireOtp) {
        saveNewProfileInfo(data);
        await updateProfileOTP();
      } else {
        await success("تم تحديث بياناتك بنجاح");
        const { email, phoneNumber, displayName } = data;
        const { id, token, cartId, userPermissions, validTo } = user;
        const updatedUser = {
          id,
          email,
          token,
          cartId,
          validTo,
          phoneNumber,
          displayName,
          userPermissions,
        };
        setUser(updatedUser);
        routeToUrl("/profile");
      }
    } catch (error) {}
  };

  const initials = user?.displayName
    ?.split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s.charAt(0))
    .join("")
    .toUpperCase();

  return (
    <>
      <Breadcrumb links={links} />

      <main className="table-card relative max-w-4xl overflow-hidden">
        <PageHero
          eyebrow="الإعدادات / الحساب"
          title="بيانات الملف الشخصي"
          icon={UserIcon}
        />

        <form
          onSubmit={handleSubmit(submit)}
          className="relative mx-auto my-6 max-w-2xl overflow-hidden"
        >
          {/* Avatar / identity card */}
          <div className="relative mb-6 flex items-center gap-4 overflow-hidden rounded-2xl border border-purple-100 bg-gradient-to-l from-purple-50/50 via-white to-purple-50/30 p-4 dark:border-purple-800/40 dark:from-purple-950/30 dark:via-gray-800 dark:to-purple-950/20">
            <div className="relative shrink-0">
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-0 mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-purple-300/40 to-transparent blur-xl dark:from-purple-700/30"
              />
              <span className="relative inline-flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-purple-700 via-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-2 ring-white/40 dark:from-purple-600 dark:via-purple-700 dark:to-purple-900 dark:ring-gray-700/40">
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-15 [background-image:radial-gradient(circle_at_1px_1px,_white_1px,_transparent_0)] [background-size:12px_12px]"
                />
                <span className="relative text-xl font-bold uppercase tracking-wide">
                  {initials || "؟"}
                </span>
              </span>
            </div>
            <div className="relative min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-400">
                مرحباً
              </p>
              <h2 className="truncate text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                {user?.displayName || "مستخدم"}
              </h2>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                {user?.email}
              </p>
            </div>
          </div>

          {/* Form fields */}
          <div className="grid gap-5 md:grid-cols-2">
            <div className="relative">
              <Input
                star
                label="اسم المستخدم"
                error={errors.displayName}
                input={register("displayName")}
                styles="input ps-10"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute start-3 top-[39px] text-gray-400 dark:text-gray-500"
              >
                <UserCircleIcon className="h-5 w-5" />
              </span>
            </div>

            <div className="relative">
              <Input
                star
                type="email"
                error={errors.email}
                label="البريد الالكتروني"
                input={register("email")}
                styles="input ps-10"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute start-3 top-[39px] text-gray-400 dark:text-gray-500"
              >
                <EnvelopeIcon className="h-5 w-5" />
              </span>
            </div>

            <div className="relative">
              <Input
                star
                dir="ltr"
                label="رقم الهاتف"
                error={errors.phoneNumber}
                input={register("phoneNumber")}
                styles="input pe-10 text-start"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute start-3 top-[39px] text-gray-400 dark:text-gray-500"
              >
                <PhoneIcon className="h-5 w-5" />
              </span>
            </div>

            <div className="relative">
              <Input
                star
                placeholder="اترك الحقل فارغاً في حالة عدم تغيير كلمة المرور"
                type={showPassword ? "text" : "password"}
                label="كلمة المرور"
                error={errors.password}
                autocomplete="new-password"
                input={register("password")}
                styles="input ps-10 pe-10"
              />
              <span
                aria-hidden
                className="pointer-events-none absolute start-3 top-[39px] text-gray-400 dark:text-gray-500"
              >
                <LockClosedIcon className="h-5 w-5" />
              </span>
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={
                  showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"
                }
                className="absolute end-2 top-[31px] flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-50 hover:text-purple-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-purple-300"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            <div className="md:col-span-2">
              <Button
                className="!w-full !py-3"
                isLoading={isSubmitting}
                title="حفظ التعديلات"
                type="submit"
              />
            </div>
          </div>
        </form>
      </main>
    </>
  );
};

export default Profile;
